import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

// Hooks (unchanged)
import { useDateRange } from './hooks/useDateRange';
import { useLiveData } from './hooks/useLiveData';
import { useGoogleAds } from './hooks/useGoogleAds';
import { useMetaAds } from './hooks/useMetaAds';
import { useGA4 } from './hooks/useGA4';
import { useFunnel } from './hooks/useFunnel';
import { useNotifications } from './hooks/useNotifications';
import { useEcommerce } from './hooks/useEcommerce';
import { useMonthlyPerformance } from './hooks/useMonthlyPerformance';

// New components
import { CommandBar } from './components/CommandBar';
import { LivePulseStrip } from './components/LivePulseStrip';
import { HeroMetrics } from './components/HeroMetrics';
import { PerformanceCanvas } from './components/PerformanceCanvas';
import { InsightsRibbon } from './components/InsightsRibbon';
import { PlatformDeepDive } from './components/PlatformDeepDive';
import { StoreFunnel } from './components/StoreFunnel';
import { TrafficOverview } from './components/TrafficOverview';
import { HistoricalData } from './components/HistoricalData';
import { NotificationDrawer } from './components/NotificationDrawer';

// Utils
import { ALLOWED_BRAND_IDS_FOR_CATEGORY_TABLE } from './utils/constants';

// External
import GlowyAIChatWidget from '../../components/GlowyAIChatWidget';

export default function OverviewV9() {
  const { user, selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId');
  const isMarketer = selectedBrand?.currentUserRole === 'MARKETER';

  // --- UI state ---
  const [activeMetric, setActiveMetric] = useState('totalSales');

  // --- Date range ---
  const {
    dateRange, startDate, endDate, isCustomDateOpen,
    setDateRange, setCustomRange, setIsCustomDateOpen,
    getComparisonWindows,
  } = useDateRange();

  // --- Data hooks ---
  const liveData = useLiveData(brandId);
  const googleAds = useGoogleAds(brandId, dateRange, startDate, endDate);
  const metaAds = useMetaAds(brandId, dateRange, startDate, endDate);
  const ga4 = useGA4(brandId, dateRange, startDate, endDate, getComparisonWindows);
  const funnel = useFunnel(brandId, dateRange, startDate, endDate);
  const notifications = useNotifications(brandId, dateRange);
  const ecommerce = useEcommerce(
    brandId, dateRange, startDate, endDate,
    {
      hasIkasConnected: liveData.hasIkasConnected,
      hasShopifyConnected: liveData.hasShopifyConnected,
      hasWooConnected: liveData.hasWooConnected,
    },
    getComparisonWindows,
  );
  const monthly = useMonthlyPerformance(brandId, dateRange, startDate, endDate);

  // --- Render ---
  return (
    <div className="overview5-bg p-4 sm:p-6 lg:p-8" id="main-content">

      {/* Section 0: CommandBar (sticky top) */}
      <CommandBar
        user={user}
        logoUrl={selectedBrand?.logoUrl}
        brandName={selectedBrand?.name}
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        isCustomDateOpen={isCustomDateOpen}
        unreadCount={notifications.unreadCount}
        onDateRangeChange={setDateRange}
        onCustomDateChange={setCustomRange}
        onToggleCustomDate={setIsCustomDateOpen}
        onNotificationToggle={notifications.toggleModal}
      />

      {/* Section 1: LivePulseStrip */}
      <LivePulseStrip
        liveGads={liveData.liveGads}
        liveMeta={liveData.liveMeta}
        liveGa4={liveData.liveGa4}
        liveIkas={liveData.liveIkas}
        liveShopify={liveData.liveShopify}
        liveWoo={liveData.liveWoo}
        hasIkasConnected={liveData.hasIkasConnected}
        hasShopifyConnected={liveData.hasShopifyConnected}
        hasWooConnected={liveData.hasWooConnected}
        isLoading={liveData.isLoading}
        updatedAt={liveData.updatedAt}
        onRefresh={liveData.refetch}
      />

      {/* Section 2: HeroMetrics (5 blended KPI) */}
      <HeroMetrics
        comparisonAggregates={ga4.comparisonAggregates}
        trendPercentages={ga4.trendPercentages}
        googleAdsSummary={googleAds.googleAdsSummary}
        metaAdsSummary={metaAds.metaAdsSummary}
        criteoAdsSummary={monthly.criteoAdsSummary}
      />

      {/* Section 3: PerformanceCanvas (chart + platform bars) */}
      <PerformanceCanvas
        activeMetric={activeMetric}
        onMetricChange={setActiveMetric}
        generateChartData={ga4.generateGA4ChartData}
        googleAdsSummary={googleAds.googleAdsSummary}
        metaAdsSummary={metaAds.metaAdsSummary}
        criteoAdsSummary={monthly.criteoAdsSummary}
        gaDevicePerf={ga4.gaDevicePerf}
        comparisonAggregates={ga4.comparisonAggregates}
      />

      {/* Section 4: InsightsRibbon */}
      <InsightsRibbon
        trendPercentages={ga4.trendPercentages}
        comparisonAggregates={ga4.comparisonAggregates}
        googleAdsSummary={googleAds.googleAdsSummary}
        metaAdsSummary={metaAds.metaAdsSummary}
        funnelSummary={funnel.funnelData?.summary}
      />

      {/* Section 5: PlatformDeepDive (accordion cards) */}
      <PlatformDeepDive
        googleAdsSummary={googleAds.googleAdsSummary}
        metaAdsSummary={metaAds.metaAdsSummary}
        criteoAdsSummary={monthly.criteoAdsSummary}
        googleAdsUnifiedData={googleAds.unifiedData}
        metaAdsUnifiedData={metaAds.unifiedData}
        gaDailyTotalsMap={ga4.gaDailyTotalsMap}
      />

      {/* Section 6: StoreFunnel */}
      <StoreFunnel
        funnelData={funnel.funnelData}
        topProducts={ecommerce.topProducts}
        categoryPerformance={ecommerce.categoryPerformance}
        isMarketer={isMarketer}
      />

      {/* Section 7: TrafficOverview */}
      <TrafficOverview
        trafficSourcesAnalytics={ga4.trafficSourcesAnalytics}
        isMarketer={isMarketer}
      />

      {/* Section 8: HistoricalData (collapsible accordions) */}
      <HistoricalData
        monthlyPerformanceData={monthly.monthlyPerformanceData}
        monthlyPlatformCategoryData={monthly.monthlyPlatformCategoryData}
        channelMonthlyRevenue={monthly.channelMonthlyRevenue}
        gaMonthlySummary={monthly.gaMonthlySummary}
        weeklyRevenueByWeekday={monthly.weeklyRevenueByWeekday}
        weeklyWoWSummary={monthly.weeklyWoWSummary}
        allCostsByMonth={monthly.allCostsByMonth}
        totalAllCosts={monthly.totalAllCosts}
        weeklyMetric={monthly.weeklyMetric}
        onWeeklyMetricChange={monthly.setWeeklyMetric}
        isVisible={ALLOWED_BRAND_IDS_FOR_CATEGORY_TABLE.has(selectedBrand?.id)}
      />

      {/* Section 9: NotificationDrawer (right slide) */}
      <NotificationDrawer
        notifications={notifications.notifications}
        isOpen={notifications.isNotificationOpen}
        onClose={notifications.toggleModal}
        onMarkRead={notifications.markAsRead}
        onMarkAllRead={notifications.markAllAsRead}
        onDelete={notifications.deleteNotification}
        unreadCount={notifications.unreadCount}
        isLoading={notifications.isLoading}
      />

      {/* AI Chat FAB */}
      <GlowyAIChatWidget />
    </div>
  );
}
